import React from 'react';
import {createPortal} from 'react-dom';
import {Sparkles, X} from 'lucide-react';
import {useQueryClient} from 'react-query';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import {useAuth} from '../authentication/AuthContext';
import {
    useAddKnowledgeTopic,
    useAddKnowledgeRssSource,
    useGenerateKnowledgeLesson,
    useKnowledgeFeedEntries,
    useKnowledgeFeedPages,
    useKnowledgeFollowingContents,
    useKnowledgeTopics,
    useSavedKnowledgeLessons,
    useSaveKnowledgeFeedEntry,
    useSaveKnowledgeLesson,
    useUnfollowKnowledgeFeed,
} from './hooks/useKnowledgeFeed';
import {useCreateNote} from '../note/hooks/useNote';
import {useSaveRecallCard} from '../recall/hooks/useRecall';
import AddRecallCardModal from '../recall/components/AddRecallCardModal';
import {useHackerNewsStories} from './hooks/useHackerNewsStories';
import {useClientSideSources} from './hooks/useClientSideSources';
import MermaidDiagram from './components/MermaidDiagram';

const SOURCE_TYPES = ['RSS feed', 'Hacker News'];
const RSS_SOURCE_TYPE = 'RSS feed';
const HN_SOURCE_TYPE = 'Hacker News';
const KAFKA_RSS_URL = 'https://kafka.apache.org/blog/feed.xml';
const FEED_FILTERS = ['All', 'For you', 'Sources', 'Saved'];
const TOPIC_OPTIONS = ['System Design', 'Go', 'React', 'TypeScript', 'AI Engineering', 'AWS', 'Docker', 'Databases', 'Software Architecture', 'Custom topic'];
const GOAL_OPTIONS = ['Learn from scratch', 'Prepare for interviews', 'Become production-ready', 'Review and retain', 'Keep up with developments'];
const LEVEL_OPTIONS = ['Beginner', 'Intermediate', 'Advanced'];
const DELIVERY_OPTIONS = ['Daily', 'Weekdays', '3 times a week', 'Weekly'];
const FORMAT_OPTIONS = ['Concept', 'Code example', 'Common mistake', 'Architecture scenario', 'Quiz', 'Industry update'];

function getBrowserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

const STATIC_PREVIEW = {
    title: 'Apache Kafka Blog',
    latestArticles: [
        'Apache Kafka 4.2 Released',
        'Improvements to Consumer Groups',
        'New Kafka Improvement Proposals',
    ],
};

function getFeedEntryTitle(entry) {
    return entry?.title ?? entry?.headline ?? entry?.name ?? 'Untitled entry';
}

function getFeedEntryMeta(entry) {
    return entry?.fetched_source ?? entry?.fetchedSource ?? entry?.source_url ?? entry?.sourceUrl ?? entry?.link ?? '';
}

function getSourceLabel(rawUrl) {
    if (!rawUrl) return 'source';

    try {
        const normalizedUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
        const hostname = new URL(normalizedUrl).hostname.replace(/^www\./i, '');
        const parts = hostname.split('.').filter(Boolean);
        if (parts.length >= 2) {
            return parts[parts.length - 2];
        }
        return parts[0] || 'source';
    } catch {
        const fallbackHost = rawUrl.replace(/^https?:\/\//i, '').split('/')[0].replace(/^www\./i, '');
        const fallbackParts = fallbackHost.split('.').filter(Boolean);
        if (fallbackParts.length >= 2) {
            return fallbackParts[fallbackParts.length - 2];
        }
        return fallbackParts[0] || 'source';
    }
}

function getFeedEntrySourceLabel(entry) {
    if (entry?.entryType === 'lesson') return 'For you';
    return getSourceLabel(getFeedEntryLink(entry) || getFeedEntryMeta(entry));
}

function getFeedEntryContent(entry) {
    if (entry?.entryType === 'lesson') {
        return [entry.why_it_matters, entry.explanation, entry.example_content, entry.senior_insight]
            .filter(Boolean)
            .join('\n\n');
    }
    return entry?.text ?? entry?.content ?? entry?.description ?? entry?.summary ?? 'No preview text available for this entry.';
}

function getFeedEntryLink(entry) {
    return entry?.source_url ?? entry?.sourceUrl ?? entry?.link ?? entry?.url ?? '';
}

function getFollowedContentType(content) {
    return content?.content_type ?? content?.contentType ?? '';
}

function getFollowedContentValue(content) {
    return content?.value ?? '';
}

function getFeedEntryPublishedAt(entry) {
    return entry?.published_at
        ?? entry?.publishedAt
        ?? entry?.published
        ?? entry?.pubDate
        ?? entry?.date
        ?? entry?.created_at
        ?? entry?.createdAt
        ?? entry?.generated_at
        ?? entry?.generatedAt
        ?? null;
}

function getFeedEntryPublishedTimestamp(entry) {
    const publishedAt = getFeedEntryPublishedAt(entry);
    if (!publishedAt) return null;

    const timestamp = new Date(publishedAt).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
}

function getFeedEntryPublishedLabel(entry) {
    const timestamp = getFeedEntryPublishedTimestamp(entry);
    if (timestamp === null) return '';

    return new Date(timestamp).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getFeedEntryDomain(entry) {
    const rawUrl = getFeedEntryLink(entry) || getFeedEntryMeta(entry);
    if (!rawUrl) return 'source';

    try {
        const normalizedUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
        const hostname = new URL(normalizedUrl).hostname;
        return hostname.replace(/^www\./i, '') || 'source';
    } catch {
        const fallbackDomain = rawUrl.replace(/^https?:\/\//i, '').split('/')[0];
        return fallbackDomain || 'source';
    }
}

function buildDomainPlaceholderImage(domain) {
    const safeDomain = (domain || 'source').slice(0, 26);
    const background = '#FB7185';
    const foreground = '#1F2937';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="64" viewBox="0 0 96 64"><rect width="96" height="64" fill="${background}"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="${foreground}" font-family="Arial" font-size="9">${safeDomain}</text></svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function getFeedEntryImage(entry) {
    return entry?.image_url ?? entry?.imageUrl ?? entry?.thumbnail_url ?? entry?.thumbnailUrl ?? buildDomainPlaceholderImage(getFeedEntryDomain(entry));
}

function getEntryKey(entry) {
    if (entry?.id) return entry.id;
    if (entry?.entryType === 'lesson') return `lesson-${getLessonIdentity(entry)}`;
    return `source-${getSourceIdentity(entry)}`;
}

function getLessonIdentity(lesson) {
    return [
        lesson?.topic_id ?? lesson?.topicId ?? '',
        lesson?.generated_at ?? lesson?.generatedAt ?? '',
        lesson?.title ?? '',
    ].join(':');
}

function getSourceIdentity(entry) {
    return [
        entry?.fetched_source ?? entry?.fetchedSource ?? '',
        entry?.source_url ?? entry?.sourceUrl ?? '',
        entry?.title ?? '',
    ].join(':');
}

export default function KnowledgeFeed() {
    const {authToken} = useAuth();
    const queryClient = useQueryClient();
    const {
        data: savedFeedEntries,
        isLoading: areSavedFeedEntriesLoading,
        error: savedFeedEntriesError,
        isFetching: areSavedFeedEntriesFetching,
    } = useKnowledgeFeedEntries(authToken);
    const {
        data: sourceFeedPages,
        isLoading: isSourceFeedLoading,
        error: sourceFeedError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetching: isSourceFeedFetching,
    } = useKnowledgeFeedPages(authToken);
    const {
        data: knowledgeTopics,
        isLoading: areTopicsLoading,
        error: topicsError,
    } = useKnowledgeTopics(authToken);
    const {
        data: savedKnowledgeLessons,
        isLoading: areLessonsLoading,
        error: lessonsError,
    } = useSavedKnowledgeLessons(authToken);
    const {
        data: followingContents,
        isLoading: isFollowingContentsLoading,
        error: followingContentsError,
        refetch: refetchFollowingContents,
    } = useKnowledgeFollowingContents(authToken);
    const addRssSource = useAddKnowledgeRssSource(authToken);
    const addKnowledgeTopic = useAddKnowledgeTopic(authToken);
    const generateKnowledgeLesson = useGenerateKnowledgeLesson(authToken);
    const saveKnowledgeLesson = useSaveKnowledgeLesson(authToken);
    const saveKnowledgeFeedEntry = useSaveKnowledgeFeedEntry(authToken);
    const unfollowFeed = useUnfollowKnowledgeFeed(authToken);
    const createNote = useCreateNote(authToken);
    const saveRecallCard = useSaveRecallCard(authToken);
    const {
        hackerNewsEnabled,
        toggleHackerNews,
        removeHackerNews,
    } = useClientSideSources();
    const {data: hnStories} = useHackerNewsStories({enabled: hackerNewsEnabled});

    const [selectedSourceType, setSelectedSourceType] = React.useState(RSS_SOURCE_TYPE);
    const [rssUrl, setRssUrl] = React.useState('');
    const [topicForm, setTopicForm] = React.useState({
        topic: 'React',
        customTopic: '',
        goal: 'Learn from scratch',
        level: 'Beginner',
        delivery: 'Daily',
        deliveryTime: '09:00',
        timezone: getBrowserTimezone(),
        formats: ['Concept'],
    });
    const [isSourcePanelOpen, setIsSourcePanelOpen] = React.useState(false);
    const [isTopicPanelOpen, setIsTopicPanelOpen] = React.useState(false);
    const [isRegisteredFeedsPanelOpen, setIsRegisteredFeedsPanelOpen] = React.useState(false);
    const [sourceError, setSourceError] = React.useState('');
    const [trackerError, setTrackerError] = React.useState('');
    const [expandedEntryKey, setExpandedEntryKey] = React.useState(null);
    const [deletingSourceUrl, setDeletingSourceUrl] = React.useState(null);
    const [creatingNoteForKey, setCreatingNoteForKey] = React.useState(null);
    const [recallModalEntry, setRecallModalEntry] = React.useState(null);
    const [activeFeedFilter, setActiveFeedFilter] = React.useState('All');
    const [topicError, setTopicError] = React.useState('');
    const [generatingTopicId, setGeneratingTopicId] = React.useState(null);
    const [generatedLessons, setGeneratedLessons] = React.useState([]);
    const [savingLessonKey, setSavingLessonKey] = React.useState(null);
    const [savingSourceKey, setSavingSourceKey] = React.useState(null);
    const [sourceSaveErrorKey, setSourceSaveErrorKey] = React.useState(null);
    const [notedEntryKeys, setNotedEntryKeys] = React.useState(() => new Set());
    const infiniteScrollTargetRef = React.useRef(null);

    const handleCreateNoteFromEntry = (entry, entryKey) => {
        setCreatingNoteForKey(entryKey);
        createNote.mutate(
            {
                title: getFeedEntryTitle(entry),
                content: getFeedEntryContent(entry),
                tags: '',
            },
            {
                onSuccess: () => setNotedEntryKeys((previous) => new Set(previous).add(entryKey)),
                onSettled: () => setCreatingNoteForKey(null),
            },
        );
    };

    const handleRecallCardSubmit = ({title, deadline, repeat, tags}) => {
        saveRecallCard.mutate(
            {front: title, back: deadline, hint: repeat, tags},
            {onSuccess: () => setRecallModalEntry(null)},
        );
    };

    const normalizedRssUrl = rssUrl.trim().toLowerCase();
    const showPreview = selectedSourceType === RSS_SOURCE_TYPE && normalizedRssUrl === KAFKA_RSS_URL;
    const previewFeedEntries = React.useMemo(() => {
        const savedSourceIdentities = new Set(savedFeedEntries.map(getSourceIdentity));
        const seen = new Set();

        return (sourceFeedPages?.pages ?? [])
            .flatMap((page) => page.items)
            .filter((entry) => {
                const identity = getSourceIdentity(entry);
                if (savedSourceIdentities.has(identity) || seen.has(identity)) return false;
                seen.add(identity);
                return true;
            });
    }, [savedFeedEntries, sourceFeedPages]);
    const hasLoadedSourcePages = (sourceFeedPages?.pages?.length ?? 0) > 0;
    const sortedFeedEntries = React.useMemo(
        () => {
            const savedLessonIdentities = new Set(savedKnowledgeLessons.map(getLessonIdentity));
            const unsavedLessons = generatedLessons.filter((lesson) => !savedLessonIdentities.has(getLessonIdentity(lesson)));

            return [
                ...savedKnowledgeLessons.map((lesson) => ({...lesson, entryType: 'lesson', isSavedLesson: true})),
                ...unsavedLessons.map((lesson) => ({...lesson, entryType: 'lesson', isSavedLesson: false})),
                ...savedFeedEntries.map((entry) => ({...entry, entryType: 'source', isSavedSource: true, isSaveableSource: true})),
                ...previewFeedEntries.map((entry) => ({...entry, entryType: 'source', isSavedSource: false, isSaveableSource: true})),
                ...hnStories.map((entry) => ({...entry, entryType: 'source', isSavedSource: false, isSaveableSource: false})),
            ].sort((left, right) => {
            const leftTimestamp = getFeedEntryPublishedTimestamp(left) ?? 0;
            const rightTimestamp = getFeedEntryPublishedTimestamp(right) ?? 0;
            return rightTimestamp - leftTimestamp;
            });
        },
        [generatedLessons, hnStories, previewFeedEntries, savedFeedEntries, savedKnowledgeLessons],
    );
    const visibleFeedEntries = React.useMemo(() => {
        if (activeFeedFilter === 'For you') return sortedFeedEntries.filter((entry) => entry.entryType === 'lesson');
        if (activeFeedFilter === 'Sources') return sortedFeedEntries.filter((entry) => entry.entryType === 'source');
        if (activeFeedFilter === 'Saved') return sortedFeedEntries.filter((entry) => entry.isSavedLesson);
        return sortedFeedEntries;
    }, [activeFeedFilter, sortedFeedEntries]);
    const registeredFeedSources = React.useMemo(
        () => followingContents
            .filter((content) => getFollowedContentType(content) === 'rss_feed')
            .map((content) => {
                const sourceUrl = getFollowedContentValue(content).trim();
                return {
                    sourceUrl,
                    sourceLabel: getSourceLabel(sourceUrl),
                };
            })
            .filter((content) => !!content.sourceUrl)
            .sort((left, right) => left.sourceLabel.localeCompare(right.sourceLabel)),
        [followingContents],
    );

    React.useEffect(() => {
        const target = infiniteScrollTargetRef.current;
        const shouldLoadSources = activeFeedFilter === 'All' || activeFeedFilter === 'Sources';
        if (!target || !shouldLoadSources || !hasNextPage || isFetchingNextPage || sourceFeedError || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) fetchNextPage();
        }, {rootMargin: '240px'});
        observer.observe(target);
        return () => observer.disconnect();
    }, [activeFeedFilter, fetchNextPage, hasNextPage, isFetchingNextPage, sourceFeedError, visibleFeedEntries.length]);

    React.useEffect(() => {
        if (!expandedEntryKey) return undefined;

        const previousOverflow = document.body.style.overflow;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setExpandedEntryKey(null);
            }
        };

        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [expandedEntryKey]);

    const updateTopicForm = (field, value) => {
        setTopicForm((previous) => ({...previous, [field]: value}));
    };

    const toggleTopicFormat = (format) => {
        setTopicForm((previous) => ({
            ...previous,
            formats: previous.formats.includes(format)
                ? previous.formats.filter((item) => item !== format)
                : [...previous.formats, format],
        }));
    };

    const handleGenerateLesson = (topicId) => {
        if (!topicId) return;
        setTopicError('');
        setGeneratingTopicId(topicId);
        generateKnowledgeLesson.mutate(topicId, {
            onSuccess: (lesson) => {
                setGeneratedLessons((previous) => [
                    lesson,
                    ...previous.filter((item) => getLessonIdentity(item) !== getLessonIdentity(lesson)),
                ]);
                setActiveFeedFilter('For you');
            },
            onError: () => setTopicError('Failed to generate a lesson. Please try again.'),
            onSettled: () => setGeneratingTopicId(null),
        });
    };

    const handleSaveLesson = (lesson) => {
        const lessonKey = getEntryKey(lesson);
        setTopicError('');
        setSavingLessonKey(lessonKey);
        saveKnowledgeLesson.mutate(lesson, {
            onSuccess: () => {
                const savedIdentity = getLessonIdentity(lesson);
                setGeneratedLessons((previous) => previous.filter((item) => getLessonIdentity(item) !== savedIdentity));
            },
            onError: () => setTopicError('Failed to save the lesson. Please try again.'),
            onSettled: () => setSavingLessonKey(null),
        });
    };

    const handleSaveSourceEntry = (entry) => {
        const entryKey = getEntryKey(entry);
        setSourceSaveErrorKey(null);
        setSavingSourceKey(entryKey);
        saveKnowledgeFeedEntry.mutate(entry, {
            onError: () => setSourceSaveErrorKey(entryKey),
            onSettled: () => setSavingSourceKey(null),
        });
    };

    const handleRetrySourceFeed = () => {
        if (sourceFeedError?.status === 400) {
            queryClient.resetQueries({queryKey: ['knowledgeFeedPages']});
            return;
        }
        fetchNextPage();
    };

    const handleAddTopic = (event) => {
        event.preventDefault();
        if (topicForm.topic === 'Custom topic' && !topicForm.customTopic.trim()) {
            setTopicError('Please enter a custom topic.');
            return;
        }
        if (topicForm.formats.length === 0) {
            setTopicError('Choose at least one lesson format.');
            return;
        }

        setTopicError('');
        addKnowledgeTopic.mutate({
            topic: topicForm.topic,
            custom_topic: topicForm.topic === 'Custom topic' ? topicForm.customTopic.trim() : null,
            goal: topicForm.goal,
            level: topicForm.level,
            delivery: topicForm.delivery,
            delivery_time: topicForm.deliveryTime,
            timezone: topicForm.timezone,
            formats: topicForm.formats,
        }, {
            onSuccess: (createdTopic) => handleGenerateLesson(createdTopic?.id),
            onError: () => setTopicError('Failed to create the learning feed. Please try again.'),
        });
    };

    const handleAddSource = () => {
        const cleanedUrl = rssUrl.trim();
        if (!cleanedUrl) {
            setSourceError('Please enter an RSS feed URL.');
            return;
        }

        setSourceError('');
        addRssSource.mutate(cleanedUrl, {
            onSuccess: () => {
                refetchFollowingContents();
            },
        });
    };

    const handleManualRefresh = () => {
        const cleanedUrl = rssUrl.trim();
        if (!cleanedUrl) {
            setSourceError('Please enter an RSS feed URL to refresh.');
            return;
        }

        setSourceError('');
        addRssSource.mutate(cleanedUrl, {
            onSuccess: () => {
                refetchFollowingContents();
            },
        });
    };

    const handleDeleteSource = (sourceUrl) => {
        if (!sourceUrl) return;

        setTrackerError('');
        setDeletingSourceUrl(sourceUrl);
        unfollowFeed.mutate(sourceUrl, {
            onSuccess: () => {
                refetchFollowingContents();
            },
            onError: () => {
                setTrackerError('Failed to delete feed source.');
            },
            onSettled: () => {
                setDeletingSourceUrl(null);
            },
        });
    };

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header/>
            <div className="flex flex-1 gap-3 bg-[var(--workspace-bg)] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                <Sidebar/>
                <div className="min-w-0 flex-1 rounded-[10px] bg-[var(--surface)] p-4 md:p-6">
                    <div className="mx-auto grid w-full max-w-7xl gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
                        <div className="order-1 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] p-3 lg:col-span-2">
                            <h1 className="text-lg font-semibold text-[var(--text-primary)]">Feed</h1>
                            <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                                Daily lessons and trusted sources, personalized around what you want to learn.
                            </p>
                        </div>

                        <div className="order-3 grid content-start gap-3 lg:col-start-2 lg:row-start-2 lg:grid-cols-1">
                            <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface)] p-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <h2 className="text-sm font-semibold text-[var(--text-primary)]">Create learning feed</h2>
                                        <p className="mt-1 text-xs leading-relaxed text-[var(--text-muted)]">
                                            Get short lessons tailored to your goals.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="shrink-0 rounded-md border border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/20 px-3 py-1.5 text-xs font-medium text-[var(--brand-coral)] transition hover:bg-[var(--brand-coral)]/30"
                                        onClick={() => setIsTopicPanelOpen((prev) => !prev)}
                                        aria-expanded={isTopicPanelOpen}
                                    >
                                        Set up
                                    </button>
                                </div>

                                {isTopicPanelOpen && (
                                    <div className="mt-4 space-y-4 border-t border-[var(--border-color)] pt-4">
                                        <form className="space-y-3" onSubmit={handleAddTopic}>
                                            <label className="block text-xs font-medium text-[var(--text-primary)]">
                                                Topic
                                                <select
                                                    value={topicForm.topic}
                                                    onChange={(event) => updateTopicForm('topic', event.target.value)}
                                                    className="mt-1 w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm outline-none focus:border-[var(--brand-coral)]"
                                                >
                                                    {TOPIC_OPTIONS.map((option) => <option key={option}>{option}</option>)}
                                                </select>
                                            </label>
                                            {topicForm.topic === 'Custom topic' && (
                                                <label className="block text-xs font-medium text-[var(--text-primary)]">
                                                    Custom topic
                                                    <input
                                                        type="text"
                                                        value={topicForm.customTopic}
                                                        maxLength={200}
                                                        onChange={(event) => updateTopicForm('customTopic', event.target.value)}
                                                        placeholder="e.g. Apache Kafka"
                                                        className="mt-1 w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm outline-none focus:border-[var(--brand-coral)]"
                                                    />
                                                </label>
                                            )}
                                            <label className="block text-xs font-medium text-[var(--text-primary)]">
                                                Goal
                                                <select
                                                    value={topicForm.goal}
                                                    onChange={(event) => updateTopicForm('goal', event.target.value)}
                                                    className="mt-1 w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm outline-none focus:border-[var(--brand-coral)]"
                                                >
                                                    {GOAL_OPTIONS.map((option) => <option key={option}>{option}</option>)}
                                                </select>
                                            </label>
                                            <div className="grid grid-cols-2 gap-2">
                                                <label className="block text-xs font-medium text-[var(--text-primary)]">
                                                    Level
                                                    <select
                                                        value={topicForm.level}
                                                        onChange={(event) => updateTopicForm('level', event.target.value)}
                                                        className="mt-1 w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-sm outline-none focus:border-[var(--brand-coral)]"
                                                    >
                                                        {LEVEL_OPTIONS.map((option) => <option key={option}>{option}</option>)}
                                                    </select>
                                                </label>
                                                <label className="block text-xs font-medium text-[var(--text-primary)]">
                                                    Delivery
                                                    <select
                                                        value={topicForm.delivery}
                                                        onChange={(event) => updateTopicForm('delivery', event.target.value)}
                                                        className="mt-1 w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-sm outline-none focus:border-[var(--brand-coral)]"
                                                    >
                                                        {DELIVERY_OPTIONS.map((option) => <option key={option}>{option}</option>)}
                                                    </select>
                                                </label>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <label className="block text-xs font-medium text-[var(--text-primary)]">
                                                    Delivery time
                                                    <input
                                                        type="time"
                                                        value={topicForm.deliveryTime}
                                                        onChange={(event) => updateTopicForm('deliveryTime', event.target.value)}
                                                        className="mt-1 w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-sm outline-none focus:border-[var(--brand-coral)]"
                                                    />
                                                </label>
                                                <label className="block text-xs font-medium text-[var(--text-primary)]">
                                                    Timezone
                                                    <input
                                                        type="text"
                                                        value={topicForm.timezone}
                                                        onChange={(event) => updateTopicForm('timezone', event.target.value)}
                                                        className="mt-1 w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-sm outline-none focus:border-[var(--brand-coral)]"
                                                    />
                                                </label>
                                            </div>
                                            <fieldset>
                                                <legend className="text-xs font-medium text-[var(--text-primary)]">Lesson formats</legend>
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {FORMAT_OPTIONS.map((format) => (
                                                        <label key={format} className="flex cursor-pointer items-center gap-1.5 rounded-full border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2.5 py-1 text-xs text-[var(--text-secondary)]">
                                                            <input
                                                                type="checkbox"
                                                                checked={topicForm.formats.includes(format)}
                                                                onChange={() => toggleTopicFormat(format)}
                                                                className="accent-[var(--brand-coral)]"
                                                            />
                                                            {format}
                                                        </label>
                                                    ))}
                                                </div>
                                            </fieldset>
                                            {topicError && <p className="text-xs text-red-300">{topicError}</p>}
                                            <button
                                                type="submit"
                                                disabled={addKnowledgeTopic.isLoading || generateKnowledgeLesson.isLoading}
                                                className="w-full rounded-md border border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/20 px-4 py-2 text-sm font-medium text-[var(--brand-coral)] transition hover:bg-[var(--brand-coral)]/30 disabled:cursor-not-allowed disabled:opacity-70"
                                            >
                                                {addKnowledgeTopic.isLoading
                                                    ? 'Creating feed...'
                                                    : generateKnowledgeLesson.isLoading
                                                        ? 'Generating first lesson...'
                                                        : 'Create feed'}
                                            </button>
                                        </form>

                                    </div>
                                )}
                            </div>

                            <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface)] p-3">
                                <div>
                                    <h2 className="text-sm font-semibold text-[var(--text-primary)]">Learning topics</h2>
                                    <p className="mt-1 text-xs leading-relaxed text-[var(--text-muted)]">
                                        Topics shaping your personalized lessons.
                                    </p>
                                </div>

                                <div className="mt-3">
                                    {areTopicsLoading ? (
                                        <p className="text-xs text-[var(--text-muted)]">Loading topics...</p>
                                    ) : topicsError ? (
                                        <p className="text-xs text-red-300">Failed to load topics.</p>
                                    ) : knowledgeTopics.length === 0 ? (
                                        <p className="text-xs text-[var(--text-muted)]">No learning topics yet.</p>
                                    ) : (
                                        <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                                            {knowledgeTopics.map((topic) => (
                                                <article
                                                    key={topic.id}
                                                    className="rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2"
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="min-w-0">
                                                            <p className="truncate text-xs font-semibold text-[var(--text-primary)]">{topic.topic_name ?? topic.topicName}</p>
                                                            <p className="mt-1 text-[11px] text-[var(--text-muted)]">{topic.level} · {topic.goal}</p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleGenerateLesson(topic.id)}
                                                            disabled={generateKnowledgeLesson.isLoading}
                                                            className="shrink-0 rounded-md border border-[var(--border-strong)] px-2 py-1 text-[11px] font-medium text-[var(--text-primary)] transition hover:bg-[var(--surface-hover)] disabled:opacity-60"
                                                        >
                                                            {generatingTopicId === topic.id ? 'Generating...' : 'New lesson'}
                                                        </button>
                                                    </div>
                                                    <p className="mt-2 text-[11px] text-[var(--text-secondary)]">
                                                        {topic.delivery} at {(topic.delivery_time ?? topic.deliveryTime ?? '').slice(0, 5)} · {(topic.formats ?? []).join(', ')}
                                                    </p>
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface)] p-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <h2 className="text-sm font-semibold text-[var(--text-primary)]">Add trusted source</h2>
                                        <p className="mt-1 text-xs leading-relaxed text-[var(--text-muted)]">
                                            Follow an RSS feed or website.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="shrink-0 rounded-md border border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/20 px-3 py-1.5 text-xs font-medium text-[var(--brand-coral)] transition hover:bg-[var(--brand-coral)]/30"
                                        onClick={() => setIsSourcePanelOpen((prev) => !prev)}
                                        aria-expanded={isSourcePanelOpen}
                                    >
                                        Add source
                                    </button>
                                </div>

                                {isSourcePanelOpen && (
                                    <div className="mt-3 space-y-4">
                                        <div className="grid gap-2 sm:grid-cols-2">
                                            {SOURCE_TYPES.map((sourceType) => {
                                                const isEnabled = sourceType === RSS_SOURCE_TYPE || sourceType === HN_SOURCE_TYPE;
                                                const isActive = selectedSourceType === sourceType;

                                                return (
                                                    <button
                                                        key={sourceType}
                                                        type="button"
                                                        disabled={!isEnabled}
                                                        onClick={() => setSelectedSourceType(sourceType)}
                                                        className={[
                                                            'rounded-md border px-3 py-2 text-left text-sm transition',
                                                            isActive
                                                                ? 'border-[var(--brand-coral)] bg-[var(--brand-coral)]/10 text-[var(--brand-coral)]'
                                                                : 'border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--text-secondary)]',
                                                            isEnabled ? 'hover:border-[var(--brand-coral)]' : 'cursor-not-allowed opacity-60',
                                                        ].join(' ')}
                                                    >
                                                        <span>{sourceType}</span>
                                                        {!isEnabled && (
                                                            <span className="ml-2 text-xs text-[var(--text-muted)]">Coming soon</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {selectedSourceType === RSS_SOURCE_TYPE && (
                                            <div className="space-y-4">
                                                <p className="text-sm text-[var(--text-muted)]">
                                                    Feed is updated manually for now. Use refresh whenever you want to fetch the latest entries.
                                                </p>
                                                <div className="space-y-2">
                                                    <label className="block text-sm font-medium text-[var(--text-primary)]">
                                                        Paste RSS feed URL
                                                    </label>
                                                    <input
                                                        type="url"
                                                        value={rssUrl}
                                                        onChange={(event) => setRssUrl(event.target.value)}
                                                        placeholder="https://kafka.apache.org/blog/feed.xml"
                                                        className="w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--brand-coral)]"
                                                    />
                                                </div>

                                                {showPreview && (
                                                    <div className="rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] p-4">
                                                        <h3 className="text-base font-semibold text-[var(--text-primary)]">{STATIC_PREVIEW.title}</h3>
                                                        <p className="mt-2 text-sm text-[var(--text-secondary)]">Latest articles:</p>
                                                        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[var(--text-primary)]">
                                                            {STATIC_PREVIEW.latestArticles.map((article) => (
                                                                <li key={article}>{article}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {sourceError && (
                                                    <p className="text-sm text-red-300">{sourceError}</p>
                                                )}

                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={handleAddSource}
                                                        disabled={addRssSource.isLoading}
                                                        className="rounded-md border border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/20 px-4 py-2 text-sm font-medium text-[var(--brand-coral)] transition hover:bg-[var(--brand-coral)]/30 disabled:cursor-not-allowed disabled:opacity-70"
                                                    >
                                                        {addRssSource.isLoading ? 'Adding source...' : 'Add source'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleManualRefresh}
                                                        disabled={addRssSource.isLoading}
                                                        className="rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-70"
                                                    >
                                                        {addRssSource.isLoading ? 'Refreshing...' : 'Refresh feed'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {selectedSourceType === HN_SOURCE_TYPE && (
                                            <div className="space-y-3">
                                                <p className="text-sm text-[var(--text-muted)]">
                                                    Hacker News top stories are fetched directly from HN and refreshed every 5 minutes.
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={toggleHackerNews}
                                                    className={[
                                                        'rounded-md border px-4 py-2 text-sm font-medium transition',
                                                        hackerNewsEnabled
                                                            ? 'border-red-400/50 bg-red-500/10 text-red-200 hover:bg-red-500/20'
                                                            : 'border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/20 text-[var(--brand-coral)] hover:bg-[var(--brand-coral)]/30',
                                                    ].join(' ')}
                                                >
                                                    {hackerNewsEnabled ? 'Remove from feed' : 'Add to feed'}
                                                </button>
                                                {hackerNewsEnabled && (
                                                    <p className="text-xs text-green-400">✓ Hacker News top stories are showing in your feed</p>
                                                )}
                                            </div>
                                        )}

                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="order-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] p-3 lg:col-start-2 lg:row-start-3">
                            <button
                                type="button"
                                className="flex w-full items-center justify-between rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm font-medium text-[var(--text-primary)] transition hover:bg-[var(--surface-hover)]"
                                onClick={() => setIsRegisteredFeedsPanelOpen((prev) => !prev)}
                            >
                                <span>Registered feeds</span>
                                <span className="text-base leading-none text-[var(--text-secondary)]">{isRegisteredFeedsPanelOpen ? '−' : '+'}</span>
                            </button>

                            {isRegisteredFeedsPanelOpen && (
                                <div className="mt-3">
                                    {trackerError && (
                                        <p className="text-sm text-red-300">{trackerError}</p>
                                    )}
                                    {isFollowingContentsLoading ? (
                                        <p className="text-sm text-[var(--text-muted)]">Loading registered feeds...</p>
                                    ) : followingContentsError ? (
                                        <p className="text-sm text-red-300">Failed to load registered feeds.</p>
                                    ) : registeredFeedSources.length === 0 && !hackerNewsEnabled ? (
                                        <p className="text-sm text-[var(--text-muted)]">No registered feeds yet.</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {hackerNewsEnabled && (
                                                <div className="flex items-center justify-between gap-3 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2">
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-[var(--text-primary)]">Hacker News</p>
                                                        <p className="text-xs text-[var(--text-muted)]">news.ycombinator.com · top stories</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={removeHackerNews}
                                                        className="rounded-md border border-red-400/50 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-200 transition hover:bg-red-500/20"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            )}
                                            {registeredFeedSources.map((source) => (
                                                <div
                                                    key={source.sourceUrl}
                                                    className="flex items-center justify-between gap-3 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2"
                                                >
                                                    <div className="min-w-0">
                                                        <p className="truncate text-sm font-medium text-[var(--text-primary)]">{source.sourceLabel}</p>
                                                        <p className="truncate text-xs text-[var(--text-muted)]">{source.sourceUrl}</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteSource(source.sourceUrl)}
                                                        disabled={deletingSourceUrl === source.sourceUrl}
                                                        className="rounded-md border border-red-400/50 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-200 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-70"
                                                    >
                                                        {deletingSourceUrl === source.sourceUrl ? 'Removing...' : 'Remove'}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="order-2 min-w-0 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] p-4 md:p-5 lg:col-start-1 lg:row-span-2 lg:row-start-2">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex flex-wrap items-center gap-1" aria-label="Feed filters">
                                    {FEED_FILTERS.map((filter, index) => (
                                        <React.Fragment key={filter}>
                                            {index > 0 && <span className="text-[var(--text-muted)]" aria-hidden="true">·</span>}
                                            <button
                                                type="button"
                                                onClick={() => setActiveFeedFilter(filter)}
                                                aria-pressed={activeFeedFilter === filter}
                                                className={[
                                                    'rounded-md px-2.5 py-1.5 text-sm font-medium transition',
                                                    activeFeedFilter === filter
                                                        ? 'bg-[var(--surface-deep)] text-[var(--text-primary)]'
                                                        : 'text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]',
                                                ].join(' ')}
                                            >
                                                {filter}
                                            </button>
                                        </React.Fragment>
                                    ))}
                                </div>
                                {((isSourceFeedFetching && !isFetchingNextPage) || areSavedFeedEntriesFetching || generateKnowledgeLesson.isLoading) && (
                                    <span className="text-xs text-[var(--text-muted)]">
                                        {generateKnowledgeLesson.isLoading ? 'Generating lesson...' : 'Refreshing...'}
                                    </span>
                                )}
                            </div>
                            {areLessonsLoading
                            || ((activeFeedFilter === 'All' || activeFeedFilter === 'Sources')
                                && (areSavedFeedEntriesLoading || isSourceFeedLoading)) ? (
                                <p className="mt-3 text-sm text-[var(--text-muted)]">Loading entries...</p>
                            ) : lessonsError
                            || ((activeFeedFilter === 'All' || activeFeedFilter === 'Sources')
                                && (savedFeedEntriesError || (sourceFeedError && !hasLoadedSourcePages))) ? (
                                <div className="mt-3 flex items-center gap-3">
                                    <p className="text-sm text-red-300">Failed to load entries.</p>
                                    {sourceFeedError && (
                                        <button
                                            type="button"
                                            onClick={() => queryClient.resetQueries({queryKey: ['knowledgeFeedPages']})}
                                            className="rounded-md border border-[var(--border-strong)] px-3 py-1.5 text-xs font-medium text-[var(--text-primary)] transition hover:bg-[var(--surface-hover)]"
                                        >
                                            Retry
                                        </button>
                                    )}
                                </div>
                            ) : visibleFeedEntries.length === 0 ? (
                                <p className="mt-3 text-sm text-[var(--text-muted)]">
                                    {activeFeedFilter === 'For you'
                                        ? 'No lessons yet. Create a learning feed to get started.'
                                        : activeFeedFilter === 'Sources'
                                            ? 'No source entries yet. Add a trusted source to get started.'
                                            : activeFeedFilter === 'Saved'
                                                ? 'No saved entries yet.'
                                                : 'No entries yet. Create a learning feed or add a source to get started.'}
                                </p>
                            ) : (
                                <div className="mt-3 space-y-2">
                                    {visibleFeedEntries.map((entry) => {
                                        const entryKey = getEntryKey(entry);
                                        const isExpanded = expandedEntryKey === entryKey;
                                        const topic = entry.entryType === 'lesson'
                                            ? knowledgeTopics.find((item) => item.id === (entry.topic_id ?? entry.topicId))
                                            : null;
                                        const sourceLabel = topic?.topic_name ?? topic?.topicName ?? getFeedEntrySourceLabel(entry);
                                        const publishedLabel = getFeedEntryPublishedLabel(entry);
                                        const isNoted = notedEntryKeys.has(entryKey);

                                        return (
                                            <div
                                                key={entryKey}
                                                className="rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] transition hover:border-[var(--brand-coral)]"
                                            >
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setExpandedEntryKey(isExpanded ? null : entryKey)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            event.preventDefault();
                                                            setExpandedEntryKey(isExpanded ? null : entryKey);
                                                        }
                                                    }}
                                                    className="w-full cursor-pointer p-3 text-left"
                                                >
                                                    <div className="flex items-start gap-3">
                                                        {entry.entryType === 'lesson' ? (
                                                            <div className="flex h-16 w-24 flex-shrink-0 items-center justify-center rounded-md border border-[var(--brand-lavender)]/30 bg-[var(--brand-lavender)]/10">
                                                                <Sparkles size={28} className="text-[var(--brand-lavender)]" aria-hidden="true"/>
                                                            </div>
                                                        ) : (
                                                            <img
                                                                src={getFeedEntryImage(entry)}
                                                                alt=""
                                                                className="h-16 w-24 flex-shrink-0 rounded-md border border-[var(--border-strong)] object-cover"
                                                                loading="lazy"
                                                                onError={(event) => {
                                                                    event.currentTarget.src = buildDomainPlaceholderImage(getFeedEntryDomain(entry));
                                                                }}
                                                            />
                                                        )}
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-[var(--text-primary)]">{getFeedEntryTitle(entry)}</p>
                                                           {(sourceLabel || publishedLabel) && (
                                                               <p className="mt-1 text-xs text-[var(--text-muted)]">
                                                                   {sourceLabel}
                                                                   {sourceLabel && publishedLabel ? ' • ' : ''}
                                                                   {publishedLabel ? `Published at ${publishedLabel}` : ''}
                                                               </p>
                                                           )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {isExpanded && createPortal(
                                                    <div
                                                        className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm md:p-8"
                                                        onClick={(event) => {
                                                            if (event.target === event.currentTarget) {
                                                                setExpandedEntryKey(null);
                                                            }
                                                        }}
                                                    >
                                                        <div
                                                            role="dialog"
                                                            aria-modal="true"
                                                            aria-labelledby={`feed-entry-title-${entryKey}`}
                                                            tabIndex={-1}
                                                            autoFocus
                                                            className="relative my-auto max-h-[calc(100vh-2rem)] w-full max-w-3xl overflow-y-auto rounded-xl border border-[var(--border-strong)] bg-[var(--surface-deep)] shadow-2xl outline-none md:max-h-[calc(100vh-4rem)]"
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => setExpandedEntryKey(null)}
                                                                className="absolute right-3 top-3 z-10 rounded-full border border-[var(--border-strong)] bg-[var(--surface)]/90 p-2 text-[var(--text-muted)] shadow-sm transition hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
                                                                aria-label="Close article"
                                                            >
                                                                <X size={18} aria-hidden="true"/>
                                                            </button>

                                                            <div className="p-4 pr-14 md:p-5 md:pr-16">
                                                                <div className="flex items-start gap-3">
                                                                    {entry.entryType === 'lesson' ? (
                                                                        <div className="flex h-16 w-24 flex-shrink-0 items-center justify-center rounded-md border border-[var(--brand-lavender)]/30 bg-[var(--brand-lavender)]/10">
                                                                            <Sparkles size={28} className="text-[var(--brand-lavender)]" aria-hidden="true"/>
                                                                        </div>
                                                                    ) : (
                                                                        <img
                                                                            src={getFeedEntryImage(entry)}
                                                                            alt=""
                                                                            className="h-16 w-24 flex-shrink-0 rounded-md border border-[var(--border-strong)] object-cover"
                                                                            onError={(event) => {
                                                                                event.currentTarget.src = buildDomainPlaceholderImage(getFeedEntryDomain(entry));
                                                                            }}
                                                                        />
                                                                    )}
                                                                    <div className="min-w-0">
                                                                        <h2 id={`feed-entry-title-${entryKey}`} className="text-base font-semibold text-[var(--text-primary)] md:text-lg">
                                                                            {getFeedEntryTitle(entry)}
                                                                        </h2>
                                                                        {(sourceLabel || publishedLabel) && (
                                                                            <p className="mt-1 text-xs text-[var(--text-muted)]">
                                                                                {sourceLabel}
                                                                                {sourceLabel && publishedLabel ? ' • ' : ''}
                                                                                {publishedLabel ? `Published at ${publishedLabel}` : ''}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="border-t border-[var(--border-strong)] px-4 pb-4 pt-4 md:px-5 md:pb-5">
                                                        {entry.entryType === 'lesson' ? (
                                                            <div className="space-y-4 text-sm leading-relaxed text-[var(--text-primary)]">
                                                                {entry.why_it_matters && (
                                                                    <section>
                                                                        <h3 className="font-semibold">Why it matters</h3>
                                                                        <p className="mt-1 whitespace-pre-wrap text-[var(--text-secondary)]">{entry.why_it_matters}</p>
                                                                    </section>
                                                                )}
                                                                {entry.explanation && (
                                                                    <section>
                                                                        <h3 className="font-semibold">Explanation</h3>
                                                                        <p className="mt-1 whitespace-pre-wrap text-[var(--text-secondary)]">{entry.explanation}</p>
                                                                    </section>
                                                                )}
                                                                {entry.example_content && (
                                                                    <section>
                                                                        <h3 className="font-semibold">{entry.example_title || 'Example'}</h3>
                                                                        <pre className="mt-2 overflow-x-auto rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-900"><code className="text-slate-900">{entry.example_content}</code></pre>
                                                                    </section>
                                                                )}
                                                                {entry.diagram && (
                                                                    <section>
                                                                        <h3 className="font-semibold">Diagram</h3>
                                                                        <div className="mt-2">
                                                                            <MermaidDiagram definition={entry.diagram}/>
                                                                        </div>
                                                                    </section>
                                                                )}
                                                                {entry.senior_insight && (
                                                                    <section className="rounded-md border border-[var(--brand-coral)]/30 bg-[var(--brand-coral)]/10 p-3">
                                                                        <h3 className="font-semibold text-[var(--brand-coral)]">Senior insight</h3>
                                                                        <p className="mt-1 whitespace-pre-wrap">{entry.senior_insight}</p>
                                                                    </section>
                                                                )}
                                                                {entry.common_mistake && (
                                                                    <section>
                                                                        <h3 className="font-semibold">Common mistake</h3>
                                                                        <p className="mt-1 whitespace-pre-wrap text-[var(--text-secondary)]">{entry.common_mistake}</p>
                                                                    </section>
                                                                )}
                                                                {entry.check_question && (
                                                                    <details className="rounded-md border border-[var(--border-strong)] p-3">
                                                                        <summary className="cursor-pointer font-semibold">Knowledge check: {entry.check_question}</summary>
                                                                        <p className="mt-2 text-[var(--text-secondary)]">{entry.check_answer}</p>
                                                                    </details>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <p className="whitespace-pre-wrap text-base leading-relaxed text-[var(--text-primary)]">{getFeedEntryContent(entry)}</p>
                                                        )}
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            {entry.entryType === 'lesson' && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSaveLesson(entry)}
                                                                    disabled={entry.isSavedLesson || savingLessonKey === entryKey}
                                                                    className="rounded-md border border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/20 px-3 py-1.5 text-xs font-medium text-[var(--brand-coral)] transition hover:bg-[var(--brand-coral)]/30 disabled:cursor-default disabled:opacity-70"
                                                                >
                                                                    {entry.isSavedLesson
                                                                        ? 'Lesson saved ✓'
                                                                        : savingLessonKey === entryKey
                                                                            ? 'Saving lesson...'
                                                                            : 'Save lesson'}
                                                                </button>
                                                            )}
                                                            {entry.entryType === 'source' && entry.isSaveableSource && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSaveSourceEntry(entry)}
                                                                    disabled={entry.isSavedSource || savingSourceKey === entryKey}
                                                                    className="rounded-md border border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/20 px-3 py-1.5 text-xs font-medium text-[var(--brand-coral)] transition hover:bg-[var(--brand-coral)]/30 disabled:cursor-default disabled:opacity-70"
                                                                >
                                                                    {entry.isSavedSource
                                                                        ? 'Article saved ✓'
                                                                        : savingSourceKey === entryKey
                                                                            ? 'Saving article...'
                                                                            : 'Save article'}
                                                                </button>
                                                            )}
                                                            {getFeedEntryLink(entry) && (
                                                                <a
                                                                    href={getFeedEntryLink(entry)}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="inline-block rounded-md border border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/20 px-3 py-1.5 text-xs font-medium text-[var(--brand-coral)] transition hover:bg-[var(--brand-coral)]/30"
                                                                >
                                                                    Go to original article
                                                                </a>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCreateNoteFromEntry(entry, entryKey)}
                                                                disabled={creatingNoteForKey === entryKey}
                                                                className="rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-1.5 text-xs font-medium text-[var(--text-primary)] transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-70"
                                                            >
                                                                {creatingNoteForKey === entryKey ? 'Creating...' : isNoted ? 'Saved as note ✓' : 'Save as note'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setExpandedEntryKey(null);
                                                                    setRecallModalEntry(entry);
                                                                }}
                                                                className="rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-1.5 text-xs font-medium text-[var(--text-primary)] transition hover:bg-[var(--surface-hover)]"
                                                            >
                                                                Add recall card
                                                            </button>
                                                        </div>
                                                        {sourceSaveErrorKey === entryKey && (
                                                            <p className="mt-2 text-xs text-red-300">Failed to save the article. Please try again.</p>
                                                        )}
                                                            </div>
                                                        </div>
                                                    </div>,
                                                    document.body,
                                                )}
                                            </div>
                                        );
                                    })}
                                    {(activeFeedFilter === 'All' || activeFeedFilter === 'Sources') && (
                                        <div ref={infiniteScrollTargetRef} className="flex min-h-12 items-center justify-center py-3">
                                            {isFetchingNextPage ? (
                                                <span className="text-xs text-[var(--text-muted)]">Loading more articles...</span>
                                            ) : sourceFeedError && hasLoadedSourcePages ? (
                                                <button
                                                    type="button"
                                                    onClick={handleRetrySourceFeed}
                                                    className="rounded-md border border-[var(--border-strong)] px-3 py-1.5 text-xs font-medium text-[var(--text-primary)] transition hover:bg-[var(--surface-hover)]"
                                                >
                                                    Try loading more
                                                </button>
                                            ) : hasNextPage ? (
                                                <span className="text-xs text-[var(--text-muted)]">Scroll for more</span>
                                            ) : previewFeedEntries.length > 0 ? (
                                                <span className="text-xs text-[var(--text-muted)]">You’re all caught up.</span>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {recallModalEntry && (
                <AddRecallCardModal
                    onClose={() => setRecallModalEntry(null)}
                    onSubmit={handleRecallCardSubmit}
                    initialFront={getFeedEntryTitle(recallModalEntry)}
                    initialBack={getFeedEntryContent(recallModalEntry)}
                    isSubmitting={saveRecallCard.isLoading}
                />
            )}
        </div>
    );
}
